import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Users, Clock, Check, X, Phone, Mail } from "lucide-react";
import { motion } from "framer-motion";

const translations = {
  he: {
    title: "סידורי הושבה מבית WiWi",
    subtitle: "באירועים מעל 400 מגיעים, נדרש דייל נוסף בתוספת 400 ₪",
    price: "1,600 ₪",
    originalPrice: "2,000 ₪",
    howItWorks: "איך זה עובד?",
    whatDoTheyDo: "מה הדיילים עושים?",
    interested: "האם תרצו לקבל פרטים נוספים על השירות?",
    yesInterested: "כן, אני מעוניין/ת לשמוע פרטים",
    noThanks: "לא, תודה",
    contactUs: "צור קשר",
    phone: "טלפון",
    email: "אימייל",
    thankYou: "תודה על ההתעניינות!",
    willContact: "נציגנו יחזרו אליכם בקרוב עם כל הפרטים.",
    desc1:
      "שבוע לפני האירוע, מסיימים עם האישורי הגעה ומקבלים קישור למערכת ההושבה הדיגיטלית. שם מושיבים את כל האורחים בשולחנות.",
    desc2: "ביום האירוע נשלחות הודעות WhatsApp עם מספרי שולחן לאורחים.",
    desc3: "2 אנשי צוות מנוסים מגיעים לאולם לפני קבלת הפנים.",
    duty1: "מקימים עמדות קבלה אטרקטיביות בכניסה לאולם",
    duty2: "פתקי הושבה וחלוקת פתקים לאורחים עם מספרי השולחנות",
    duty3:
      "רישום ותיעוד של האורחים שהגיעו בפועל ועדכון הזוג ביום למחרת עם דוח מסודר ומפורט",
    duty4:
      "סידור הכיסאות בהתאם לכמות האנשים שהגיעו במהלך הכניסה של האורחים לחופה",
    duty5:
      "הכוונת האורחים לשולחנות לאחר החופה על ידי דיילות ייצוגיות שיעמדו בכניסה לאולם",
    duty6: "צמצום מקומות ישיבה (חורים) בתוך האולם",
    duty7: "מניעת פתיחת שולחנות רזרבה שלא לצורך",
    duty8:
      "מוודאים עם מנהל האירוע איפה כל האורחים יושבים ולא הולכים לשום מקום עד שלכל אורח יש מקום ישיבה",
  },
  en: {
    title: "WiWi Seating Arrangements",
    subtitle:
      "For events with over 400 guests, an additional usher is required at an extra cost of ₪400.",
    price: "₪1,600",
    originalPrice: "₪2,000",
    howItWorks: "How it works?",
    whatDoTheyDo: "What do the ushers do?",
    interested: "Would you like to receive more details about this service?",
    yesInterested: "Yes, I am interested in hearing more details",
    noThanks: "No, thank you",
    contactUs: "Contact Us",
    phone: "Phone",
    email: "Email",
    thankYou: "Thank you for your interest!",
    willContact:
      "Our representative will get back to you soon with all the details.",
    desc1:
      "One week before the event, guest confirmations are finalized, and you receive a link to the digital seating system. There, all guests are assigned to tables.",
    desc2:
      "On the day of the event, WhatsApp messages are sent to guests with their table numbers.",
    desc3:
      "Two experienced staff members arrive at the venue before the reception.",
    duty1: "Set up attractive reception stands at the entrance to the hall",
    duty2:
      "Prepare seating cards and distribute them to guests with table numbers",
    duty3:
      "Register and document guests who actually arrived and update the couple the next day with an organized and detailed report",
    duty4:
      "Arrange chairs according to the number of people who arrived during guests' entry to the chuppah",
    duty5:
      "Direct guests to tables after the chuppah by representative ushers who will stand at the entrance to the hall",
    duty6: "Reduce empty seats (gaps) within the hall",
    duty7: "Prevent unnecessary opening of reserve tables",
    duty8:
      "Confirm with the event manager where all guests are seated and do not leave until every guest has a seat",
  },
};

export default function SeatingServicePage() {
  const [language, setLanguage] = useState("he");
  const [clientPhone, setClientPhone] = useState("");
  const [isInterested, setIsInterested] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);

  const t = translations[language];
  const isRTL = language === "he";

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const languageParam = urlParams.get("language");
    const phoneParam = urlParams.get("client_phone");
    const encodedPhoneParam = urlParams.get("p");

    if (languageParam && (languageParam === "en" || languageParam === "he")) {
      setLanguage(languageParam);
    }

    if (encodedPhoneParam) {
      try {
        const decodedPhone = atob(encodedPhoneParam);
        if (/^\d{10}$/.test(decodedPhone)) {
          setClientPhone(decodedPhone);
        }
      } catch (e) {
        console.error("Failed to decode phone parameter", e);
      }
    } else if (phoneParam) {
      setClientPhone(phoneParam);
    }
  }, []);

  const handleResponse = (interested) => {
    setIsInterested(interested);
    if (interested) {
      // כאן תשלח את המידע לשרת
      console.log("Client interested in seating service:", {
        clientPhone,
        interested: true,
      });
      setShowThankYou(true);
    }
  };

  if (showThankYou) {
    return (
      <div
        className={`min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 flex items-center justify-center p-4`}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 max-w-md text-center"
        >
          <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t.thankYou}
          </h2>
          <p className="text-gray-600 mb-8">{t.willContact}</p>

          <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 mb-3">{t.contactUs}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-center gap-2">
                <Phone className="w-4 h-4 text-rose-500" />
                <span dir="ltr">03-1234567</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Mail className="w-4 h-4 text-rose-500" />
                <span dir="ltr">support@wiwi.co.il</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 py-8 px-4`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <img
            src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/dbce96e59_wiwilogo.jpg"
            alt="WiWi Logo"
            className="h-16 md:h-20 mx-auto mb-6"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-8 md:p-12"
        >
          <div className="text-center mb-8">
            <Users className="w-12 h-12 text-rose-500 mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
              {t.title}
            </h2>
            <div className="flex items-baseline justify-center gap-2 mb-6">
              <span className="text-gray-400 line-through text-xl md:text-2xl">
                {t.originalPrice}
              </span>
              <span className="text-3xl md:text-4xl font-bold text-rose-600">
                {t.price}
              </span>
            </div>
            <p className="text-gray-600 mb-8 text-sm md:text-base">
              {t.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-8">
            <div className="bg-rose-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-rose-900 mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6" />
                {t.howItWorks}
              </h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start gap-3">
                  <Check
                    className={`w-5 h-5 text-green-500 mt-0.5 flex-shrink-0 ${
                      isRTL ? "ml-0" : "mr-0"
                    }`}
                  />
                  <p>{t.desc1}</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check
                    className={`w-5 h-5 text-green-500 mt-0.5 flex-shrink-0 ${
                      isRTL ? "ml-0" : "mr-0"
                    }`}
                  />
                  <p>{t.desc2}</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check
                    className={`w-5 h-5 text-green-500 mt-0.5 flex-shrink-0 ${
                      isRTL ? "ml-0" : "mr-0"
                    }`}
                  />
                  <p>{t.desc3}</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                <Users className="w-6 h-6" />
                {t.whatDoTheyDo}
              </h3>
              <div className="space-y-2 text-gray-700 text-sm">
                {[
                  t.duty1,
                  t.duty2,
                  t.duty3,
                  t.duty4,
                  t.duty5,
                  t.duty6,
                  t.duty7,
                  t.duty8,
                ].map((duty, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check
                      className={`w-4 h-4 text-green-500 mt-0.5 flex-shrink-0 ${
                        isRTL ? "ml-0" : "mr-0"
                      }`}
                    />
                    <p>{duty}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-rose-100 to-pink-100 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">
              {t.interested}
            </h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                onClick={() => handleResponse(true)}
                className="flex-1 h-12 bg-rose-500 hover:bg-rose-600 text-white rounded-lg"
              >
                <Check className={`w-5 h-5 ${isRTL ? "ml-2" : "mr-2"}`} />
                {t.yesInterested}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleResponse(false)}
                className="flex-1 h-12 border-gray-300 hover:bg-gray-100 rounded-lg"
              >
                <X className={`w-5 h-5 ${isRTL ? "ml-2" : "mr-2"}`} />
                {t.noThanks}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
